import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  useUserConfigurations,
  useConfigurations,
} from "@via/api/configuration";
import {
  ACTIVITY_NAVIGATION,
  NAVIGATION,
  WORKBENCH_NAVIGATION,
  AGENT_WORKSPACES_NAVIGATION,
  CORTEX_NAVIGATION,
  SCAN_NAVIGATION,
} from "@via/utils/constants.tsx";
import { isScanAvailable, withScanNavigation } from "@via/utils/scan";
import { classNames, isCurrent } from "@via/utils/helpers.tsx";
import { Fragment, useMemo, type JSX } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "/logo.svg";
import { useAuth } from "@via/hooks/useAuth";
import { useGlobal } from "@via/hooks/useGlobal";
import { defaultOrganizationFor } from "@via/hooks/organization-access.ts";
import { BuildingOfficeIcon } from "@heroicons/react/24/outline";

export default function MobileMenu({
  mobileSidebarOpen,
  setMobileSidebarOpen,
}): JSX.Element {
  const { pathname } = useLocation();

  const { user } = useAuth();
  const { selectedOrganization } = useGlobal();
  const { data: configurations } = useUserConfigurations();
  const { data: mainConfigurations } = useConfigurations();

  const navigationOptions = useMemo(() => {
    let options = [...NAVIGATION];

    if (user && configurations) {
      // The organization last worked in, not whichever membership sorts first (#1069).
      const organizationId =
        defaultOrganizationFor(user, selectedOrganization?.id)?.id || "";
      const isOrganizationActive = organizationId !== "";
      if (
        user.registrationType !== null &&
        organizationId &&
        isOrganizationActive
      ) {
        const ORGANIZATION_NAVIGATION = {
          name: "Organization",
          href: `/organization/${organizationId}`,
          icon: BuildingOfficeIcon,
        };
        options = [ORGANIZATION_NAVIGATION, ...options];
      } else {
        options = [ACTIVITY_NAVIGATION, ...options];
      }

      if (configurations.AGENT_WORKSPACE_JOBS_ENABLED) {
        options = [...options, AGENT_WORKSPACES_NAVIGATION];
      }

      if (configurations.SHOW_WORKBENCH) {
        options = [...options, WORKBENCH_NAVIGATION];
      }

      if (user?.featureFlags?.FF_MODEL_SMITH) {
        options = [...options, CORTEX_NAVIGATION];
      }
    }
    return withScanNavigation(
      options,
      SCAN_NAVIGATION,
      isScanAvailable(mainConfigurations)
    );
  }, [configurations, mainConfigurations, user, selectedOrganization?.id]);

  return (
    <Transition.Root show={mobileSidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-20 lg:hidden"
        onClose={setMobileSidebarOpen}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-900/80" />
        </Transition.Child>

        <div className="fixed inset-0 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full">
            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    className="-m-2.5 p-2.5"
                    onClick={() => {
                      setMobileSidebarOpen(false);
                    }}>
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                <div className="flex h-16 shrink-0 items-center">
                  <Link to="/" target="_blank">
                    <img
                      className="h-8 w-auto"
                      src={Logo}
                      alt="Foundry Connect logo"
                    />
                  </Link>
                </div>
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul role="list" className="-mx-2 mt-2 space-y-1">
                        {navigationOptions.map(item => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={classNames(
                                isCurrent(item.href, pathname)
                                  ? "bg-gray-50 text-indigo-600"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600",
                                "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6"
                              )}>
                              <item.icon
                                className={classNames(
                                  isCurrent(item.href, pathname)
                                    ? "text-indigo-600"
                                    : "text-gray-400 group-hover:text-indigo-600",
                                  "h-6 w-6 shrink-0"
                                )}
                                aria-hidden="true"
                              />
                              {item.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
